import { useState, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, FileText, ExternalLink, Search, Calendar, TrendingUp } from "lucide-react";
import { useHubSECFilings, type HubSECFiling } from "@/hooks/useHubResearch";

function getFilingBadgeColor(type: string | null): string {
  if (!type) return "bg-gray-100 text-gray-800";
  const t = type.toUpperCase();
  if (t.includes("10-K")) return "bg-blue-100 text-blue-800";
  if (t.includes("10-Q")) return "bg-cyan-100 text-cyan-800";
  if (t.includes("8-K")) return "bg-amber-100 text-amber-800";
  if (t.includes("S-1") || t.includes("S-3")) return "bg-purple-100 text-purple-800";
  if (t.includes("DEF 14A") || t.includes("PROXY")) return "bg-green-100 text-green-800";
  return "bg-gray-100 text-gray-800";
}

interface SECFilingsTabProps {
  accountId: string;
  companyName: string;
  hubAccountName: string | null;
}

export function SECFilingsTab({ accountId, companyName, hubAccountName }: SECFilingsTabProps) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const { data: filings, isLoading } = useHubSECFilings(companyName, hubAccountName);

  const filingTypes = useMemo(() => {
    if (!filings?.length) return [];
    const types = new Set(filings.map(f => f.filing_type).filter(Boolean));
    return Array.from(types).sort() as string[];
  }, [filings]);

  const filtered = useMemo(() => {
    if (!filings) return [];
    return filings.filter(f => {
      if (typeFilter !== "all" && f.filing_type !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (f.description?.toLowerCase().includes(q) || f.ai_summary?.toLowerCase().includes(q) || f.filing_type?.toLowerCase().includes(q));
      }
      return true;
    });
  }, [filings, search, typeFilter]);

  const stats = useMemo(() => {
    if (!filings?.length) return { total: 0, types: {} as Record<string, number>, latest: null as string | null, ticker: null as string | null };
    const types: Record<string, number> = {};
    filings.forEach(f => {
      const t = f.filing_type || "Other";
      types[t] = (types[t] || 0) + 1;
    });
    return {
      total: filings.length,
      types,
      latest: filings[0]?.filing_date || null,
      ticker: filings[0]?.ticker || null,
    };
  }, [filings]);

  if (isLoading) return <div className="flex items-center justify-center h-[300px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (!filings?.length) return <div className="p-8 text-center"><FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" /><h3 className="text-lg font-medium mb-2">No SEC Filings Found</h3><p className="text-muted-foreground text-sm">No SEC filings matched for {companyName}</p></div>;

  return (
    <div className="p-6 space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><FileText className="h-4 w-4" /><span className="text-xs font-medium">Total Filings</span></div><p className="text-2xl font-bold">{stats.total}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs font-medium">Filing Types</span></div><p className="text-2xl font-bold">{Object.keys(stats.types).length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><Calendar className="h-4 w-4" /><span className="text-xs font-medium">Latest Filing</span></div><p className="text-sm font-bold">{stats.latest ? new Date(stats.latest).toLocaleDateString() : "—"}</p></CardContent></Card>
        <Card><CardContent className="p-4"><div className="flex items-center gap-2 text-muted-foreground mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs font-medium">Ticker</span></div><p className="text-2xl font-bold">{stats.ticker || "—"}</p></CardContent></Card>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search filings..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder="Filing Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {filingTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-muted-foreground">{filtered.length} of {filings.length} filings</div>

      <div className="divide-y border rounded-lg">
        {filtered.map(filing => (
          <div key={filing.id} className="p-4 hover:bg-muted/50 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className={`text-xs ${getFilingBadgeColor(filing.filing_type)}`}>{filing.filing_type || "Unknown"}</Badge>
                  {filing.filing_date && <span className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(filing.filing_date).toLocaleDateString()}</span>}
                </div>
                <p className="text-sm font-medium">{filing.description || filing.accession_number || "SEC Filing"}</p>
                {filing.ai_summary && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{filing.ai_summary}</p>}
              </div>
              {filing.document_url && (
                <a href={filing.document_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary shrink-0">
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
